// Pure maths for the Route Map trip tape. No React, no DOM, no chart library.
//
// Everything the tape draws is a percentage of one time window, so this module owns exactly two
// ideas: mapping a timestamp onto 0..100, and answering "where was the vehicle at time T".
//
// Timestamps are epoch MILLISECONDS throughout. The trail endpoint sends ISO strings; they are
// parsed once in normaliseSamples and never re-parsed, because the readout runs at ~12 Hz.

import moment from 'moment-timezone';

/**
 * Refuse to interpolate across a gap wider than this — snap to the earlier sample instead.
 *
 * One hour, matching the prototype. The server independently marks samples whose preceding
 * interval exceeded its own measured cap (`interpolated`), which is a tighter and better-informed
 * test; this is the client-side floor for the case where a viewer scrubs into a hole the server
 * did not flag.
 */
export const GAP_MS = 3600000;

/** A window narrower than this is a mis-click, not an intent. */
export const MIN_WINDOW_MS = 5000;

/** A stationary trip must not divide the speed lane by ~0. */
export const SPEED_FLOOR_KMH = 10;

/**
 * Wall-clock milliseconds between React commits while the playhead moves.
 *
 * The playhead itself moves every frame by a CSS transform, which costs no React work at all. This
 * throttles only the things that must re-render — the readout, the map marker, the progress line —
 * to ~12 Hz regardless of frame rate. Exported so the test can assert the bound rather than
 * guessing at it.
 */
export const COMMIT_MS = 80;

/** Playback rates the tape offers, and the one it opens on. */
export const PLAY_RATES = Object.freeze([5, 15, 30]);
export const PLAY_RATE = 30;

/** Horizon motion-state codes. `cls` suffixes a CSS class; `label` is what the readout says. */
export const MOTION_STATES = Object.freeze({
    100: { label: 'Stop', cls: 'stop' },
    150: { label: 'Movement detected', cls: 'movement' },
    200: { label: 'In motion', cls: 'motion' },
    300: { label: 'In tow', cls: 'tow' },
    400: { label: 'Idle', cls: 'idle' },
    450: { label: 'Excessive idle', cls: 'idle-excessive' },
    460: { label: 'Excusable idle', cls: 'idle-excusable' },
    700: { label: 'Crash', cls: 'crash' },
    800: { label: 'EV standby', cls: 'ev-standby' },
    850: { label: 'Hybrid standby', cls: 'hybrid-standby' },
});

/** Event types the tape ticks, in the order the legend lists them. */
export const TAPE_EVENTS = Object.freeze({
    HARSH_ACCEL: { label: 'Harsh acceleration', cls: 'accel' },
    HARSH_BRAKE: { label: 'Harsh braking', cls: 'brake' },
    HARSH_CORNER: { label: 'Harsh cornering', cls: 'corner' },
    OVERSPEED: { label: 'Overspeeding', cls: 'overspeed' },
});

const num = (value) => (Number.isFinite(Number(value)) && value !== null ? Number(value) : null);

/**
 * Trail samples → the internal row shape, ascending by time.
 *
 * `ms` is parsed once here. Rows with an unparseable timestamp are dropped rather than sorted to
 * 1970, which would put a phantom sample at the head of every tape.
 */
export const normaliseSamples = (samples) => (Array.isArray(samples) ? samples : [])
    .map((sample) => ({
        ms: Date.parse(sample?.ts),
        lat: num(sample?.lat),
        lon: num(sample?.lon),
        kmh: num(sample?.kmh),
        code: num(sample?.motionInference),
        heading: num(sample?.heading),
        litre: num(sample?.fuelLevelLitre),
        // Independent of `litre`: an ELD asset reports percent only, so neither implies the other.
        pct: num(sample?.fuelLevelPct),
        soc: num(sample?.soc),
        // Server-marked: the interval *preceding* this sample is unobserved.
        gapBefore: sample?.interpolated === true,
    }))
    .filter((row) => Number.isFinite(row.ms))
    .sort((a, b) => a.ms - b.ms);

/**
 * Position of `ms` across `[fromMs, toMs]`, as a percentage clamped to 0..100.
 *
 * The `Math.max(1, span)` guard is load-bearing: a single-sample trip has fromMs === toMs, and
 * without it every width and offset on the tape would be NaN%, which renders as nothing at all.
 */
export const xPct = (ms, fromMs, toMs) => {
    const span = Math.max(1, toMs - fromMs);
    const pct = ((ms - fromMs) / span) * 100;
    if (!Number.isFinite(pct)) return 0;
    return Math.min(100, Math.max(0, pct));
};

/** Whole-trip peak speed with a floor. NOT recomputed per zoom window — see buildSpeedPoints. */
export const speedCeiling = (rows) => rows.reduce(
    (peak, row) => (row.kmh !== null && row.kmh > peak ? row.kmh : peak),
    SPEED_FLOOR_KMH,
);

/**
 * One band per consecutive sample pair, each coloured by the state of the EARLIER sample.
 *
 * That earlier-sample rule is shared with `positionAt`, deliberately: if they diverged, the state
 * chip in the readout would name a different state from the band sitting under the playhead — a bug
 * nobody reports and everybody sees.
 */
export const buildRibbon = (rows) => {
    const bands = [];
    for (let index = 1; index < rows.length; index += 1) {
        bands.push({
            fromMs: rows[index - 1].ms,
            toMs: rows[index].ms,
            code: rows[index - 1].code,
            // A band whose right edge is a flagged sample spans unobserved time.
            unobserved: rows[index].gapBefore,
        });
    }
    return bands;
};

/**
 * The speed lane as an SVG `points` string in a fixed 1000x100 viewBox.
 *
 * The viewBox plus `preserveAspectRatio="none"` is what makes the lane resolution-independent
 * without measuring anything: x is the percentage times ten, y is the speed inverted against the
 * ceiling. The polyline needs `vector-effect="non-scaling-stroke"` in the markup, or the
 * non-uniform scale stretches the 1px stroke into a wedge.
 *
 * `ceiling` is the whole trip's peak, so zooming pans the lane without rescaling it — a 60 km/h
 * stretch must not look like a 120 km/h one just because you zoomed into it.
 */
export const buildSpeedPoints = (rows, fromMs, toMs, ceiling) => rows
    .filter((row) => row.kmh !== null)
    .map((row) => {
        const x = xPct(row.ms, fromMs, toMs) * 10;
        const y = 100 - (row.kmh / Math.max(ceiling, 1)) * 100;
        return `${x.toFixed(2)},${Math.min(100, Math.max(0, y)).toFixed(2)}`;
    })
    .join(' ');

/** Index of the last row at or before `ms`, or -1. Binary search; the one in this module. */
export const indexAtOrBefore = (rows, ms) => {
    let lo = 0;
    let hi = rows.length - 1;
    let found = -1;
    while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2);
        if (rows[mid].ms <= ms) {
            found = mid;
            lo = mid + 1;
        } else hi = mid - 1;
    }
    return found;
};

/** Great-circle initial bearing, for the ~2% of samples with no reported heading. */
const bearingDeg = (a, b) => {
    const toRad = Math.PI / 180;
    const lat1 = a.lat * toRad;
    const lat2 = b.lat * toRad;
    const dLon = (b.lon - a.lon) * toRad;
    const y = Math.sin(dLon) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
    return (Math.atan2(y, x) / toRad + 360) % 360;
};

const snap = (row, index, reason) => ({
    ms: row.ms,
    lat: row.lat,
    lon: row.lon,
    kmh: row.kmh,
    code: row.code,
    soc: row.soc,
    litre: row.litre,
    pct: row.pct,
    heading: row.heading,
    sampleIndex: index,
    reason,
});

/**
 * Where the vehicle was at `ms`.
 *
 * `reason` is a discriminated outcome, not a boolean, because the four cases mean different things
 * to the reader:
 *
 *  - `exact`   — at or beyond an endpoint of the trip. Nothing is wrong.
 *  - `interp`  — between two samples, interpolated.
 *  - `gap`     — the bracketing samples straddle an outage, so the position is the last known fix.
 *  - `no-fix`  — a bracketing sample has no coordinates.
 *
 * Only `gap` and `no-fix` warrant a warning in the readout. The prototype conflated all of them
 * into `interp: false` and so showed "snapped" the instant a trip was selected, before the user had
 * touched anything.
 *
 * `lat`/`lon`/`kmh` interpolate; `code`/`soc`/`litre`/`pct` are taken from the earlier sample
 * unchanged — a fuel level is a reading, not a trend, and interpolating it would invent numbers.
 */
export const positionAt = (rows, ms) => {
    if (!rows || rows.length === 0) return null;
    if (ms <= rows[0].ms) return snap(rows[0], 0, 'exact');
    if (ms >= rows[rows.length - 1].ms) return snap(rows[rows.length - 1], rows.length - 1, 'exact');

    const lower = indexAtOrBefore(rows, ms);
    const a = rows[lower];
    const b = rows[lower + 1];
    const dt = b.ms - a.ms;

    if (dt <= 0 || dt > GAP_MS || b.gapBefore) return snap(a, lower, 'gap');
    if (a.lat === null || a.lon === null || b.lat === null || b.lon === null) {
        return snap(a, lower, 'no-fix');
    }

    const f = (ms - a.ms) / dt;
    const lerp = (from, to) => from + (to - from) * f;
    let kmh = null;
    if (a.kmh !== null && b.kmh !== null) kmh = lerp(a.kmh, b.kmh);
    else if (a.kmh !== null) kmh = a.kmh;
    else if (b.kmh !== null) kmh = b.kmh;

    return {
        ms,
        lat: lerp(a.lat, b.lat),
        lon: lerp(a.lon, b.lon),
        kmh,
        code: a.code,
        soc: a.soc,
        litre: a.litre,
        pct: a.pct,
        heading: b.heading ?? a.heading ?? bearingDeg(a, b),
        sampleIndex: lower,
        reason: 'interp',
    };
};

/**
 * Validate and clamp a requested zoom window against the trip's own bounds.
 *
 * Reversed inputs are swapped rather than rejected — the user's intent is unambiguous and refusing
 * it teaches nothing.
 *
 * @returns {{window: ?{fromMs: number, toMs: number}, error: ?string}}
 */
export const clampWindow = (fromMs, toMs, tripFromMs, tripToMs) => {
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) {
        return { window: null, error: 'Pick both a start and an end.' };
    }

    let lo = Math.min(fromMs, toMs);
    let hi = Math.max(fromMs, toMs);
    lo = Math.max(lo, tripFromMs);
    hi = Math.min(hi, tripToMs);

    if (hi - lo < MIN_WINDOW_MS) {
        return { window: null, error: 'The range must be at least 5 seconds.' };
    }
    return { window: { fromMs: lo, toMs: hi }, error: null };
};

/**
 * `<input type="datetime-local">` value → epoch ms, read in the AGENCY timezone.
 *
 * The input emits a bare wall clock with no zone, and the agency timezone is frequently not the
 * browser's. Reading it with `new Date(value)` would silently shift every jump and zoom by the
 * offset between them — eight hours, for a Malaysian agency on a European laptop.
 */
export const parseTapeInput = (value, timezone) => {
    if (!value) return NaN;
    const parsed = moment.tz(value, ['YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DDTHH:mm'], timezone || 'UTC');
    return parsed.isValid() ? parsed.valueOf() : NaN;
};

/** Epoch ms → a `datetime-local` value in the agency timezone. The inverse of parseTapeInput. */
export const formatTapeInput = (ms, timezone) => (Number.isFinite(ms)
    ? moment.tz(ms, timezone || 'UTC').format('YYYY-MM-DDTHH:mm:ss')
    : '');

/** Readout and axis timestamps, DD-MM-YYYY. Seconds matter here — this is a scrubber. */
export const formatTapeClock = (ms, timezone) => (Number.isFinite(ms)
    ? moment.tz(ms, timezone || 'UTC').format('DD-MM-YYYY HH:mm:ss')
    : '—');

/**
 * Put a time-of-day on the right side of midnight for a trip that crosses it.
 *
 * The tape's fields pick a clock, not a date — one trip is the whole context, so a calendar there
 * is a question the reader has already answered. That works because a trip almost always sits
 * inside one day, and where it does not, the time alone is genuinely ambiguous: on a 23:50 → 00:20
 * run, "00:10" reads as ten past midnight *before* the trip began, ten hours adrift and rejected
 * as outside it.
 *
 * A trip is bounded well under 24 hours, so at most one of the two candidate days can land inside
 * the window — there is nothing to choose between. Anything already inside is returned untouched,
 * and a time that belongs to neither day is left alone for the caller's own validation to report.
 *
 * Adding a calendar day rather than 24 hours: an agency on a DST-observing zone has days that are
 * 23 and 25 hours long, and the clock the reader picked is the thing to preserve.
 */
export const snapTimeIntoWindow = (value, options) => {
    const { fromMs, toMs, timezone } = options || {};
    const zone = timezone || 'UTC';
    const ms = parseTapeInput(value, zone);
    if (!Number.isFinite(ms) || !Number.isFinite(fromMs) || !Number.isFinite(toMs)) return value;
    if (ms >= fromMs && ms <= toMs) return value;

    const shifted = moment.tz(value, 'YYYY-MM-DDTHH:mm:ss', zone).add(1, 'day');
    if (!shifted.isValid()) return value;

    const shiftedMs = shifted.valueOf();
    return shiftedMs >= fromMs && shiftedMs <= toMs
        ? shifted.format('YYYY-MM-DDTHH:mm:ss')
        : value;
};
